import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import momentPropTypes from 'react-moment-proptypes';

import Icon from '../Icon';
import Link from '../Link';
import Loader from '../Loader';
import TranslationProvider, {
  getTranslation,
} from '../../providers/TranslationProvider';
import ReservationButtonList from '../ReservationButtonList';
import VenueShape from '../types/venue';
import ReservationShape from '../types/reservations';
import Chevron from '../../svg/icons/ic_chevron-left_20x20.svg';
import Warning from '../../svg/icons/warning.svg';
import CONTEXT from '../ReservationButtonList/constants';
import Info from '../../svg/icons/ic_info-outline_20x20.svg';
import PlatinumVenueShift from '../PlatinumVenueShift';
import Button from '../Button';
import { ALL_DAY } from '../../constants/resyCore';
import NotifyUnavailableModal from '../NotifyUnavailableModal';
import TableNotAvailable from '../TableNotAvailable';
import { checkIfWidget } from '../../utils';

import './ShiftInventory.scss';

const ALL_SERVICE_TYPES = 'all';
const ALL_TABLE_TYPES = 'all';
const PLATINUM_EXCLUSIVE_ID = 5035;

const tableIsUnavailableMessage = (
  <TranslationProvider translationKey="table_config_id_not_found">
    <div id="table-not-available">
      We&apos;re sorry, the table you were looking for is no longer available. Please see below for our current availability.
    </div>
  </TranslationProvider>
);

const testId = 'shift-inventory';

const InfoIcon = () => (
  <Icon
    className="info-icon"
    width="17.5px"
    height="17.5px"
    component={Info}
    name="info"
    aria-hidden="true"
  />
);

const ShiftInventory = ({
  activeNotifications,
  Analytics,
  doubleBookingError,
  clearTimeFilter,
  filterQuery,
  isClosedOnSelectedDate,
  isGdaUser,
  isInventoryAvailableForDate,
  isVenueClosed,
  largePartyMessage,
  loadingReservations,
  maxQuantity,
  nextAvailableDate,
  nextAvailableDateFormatted,
  notifyData,
  onDateSelect,
  onReservationSelect,
  platinumNight,
  recommendedVenues,
  reservations,
  selectedDateFormatted,
  selectedQuantity,
  selectedTimeFormatted,
  tableIsUnavailable,
  venue,
  eventsExistOnDay,
  shopSmallValue,
  centurionNyValue,
  centNyServiceTypes,
  centNyTableTypes,
}) => {
  const hasReservationSlots = reservations?.some((res) => res?.available?.length);

  const centNyServiceTypesArr = centNyServiceTypes !== ALL_SERVICE_TYPES ? centNyServiceTypes.split(',') : [];
  const centNyTableTypesArr = centNyTableTypes !== ALL_TABLE_TYPES ? centNyTableTypes.split(',') : [];

  const buildViewAllLink = (relativePath) => {
    if (checkIfWidget()) return `//resy.com${relativePath}`;
    return relativePath;
  };

  const mappedReservations = () => reservations?.map((shift, index) => {
    const { serviceTypeName, serviceTypeId } = shift;

    if (!serviceTypeName || (shopSmallValue === CONTEXT.SHOP_SMALL && serviceTypeId !== 2)
    || (centurionNyValue && centNyServiceTypesArr.length > 0 && !centNyServiceTypesArr.includes(serviceTypeName))
    ) {
      return null;
    }

    let clearFilterButton;

    if (filterQuery.time_filter) {
      clearFilterButton = (
        <Button
          onClick={clearTimeFilter}
          className="ShiftInventory__clear-filter"
          variant="link"
          aria-label={getTranslation({
            translationKey: 'all_times_for_service',
            translationValues: { serviceTypeName },
            fallback: `All times for ${serviceTypeName}`,
          })}
        >
          <TranslationProvider translationKey="all_times">
            <span>All Times &nbsp;</span>
          </TranslationProvider>
          <Icon component={Chevron} name="Chevron" />
        </Button>
      );
    }

    const renderReservationButtonList = (slots) => (
      <>
        <h2 className="ShiftInventory__shift__title">
          {serviceTypeName}
        </h2>
        <ReservationButtonList
          Analytics={Analytics}
          activeNotifications={activeNotifications}
          className="ShiftInventory__shift__slots"
          clearFilterButton={clearFilterButton}
          context={CONTEXT.INLINE_BOOKING}
          featureRecaptcha={venue.featureRecaptcha}
          onReservationSelect={onReservationSelect}
          reservations={slots}
          serviceTypeId={shift.serviceTypeId}
          serviceTypeName={serviceTypeName}
          venueId={venue.id}
          venueCity={venue.location.city}
          venueName={venue.name}
          centurionNyValue={centurionNyValue}
          centNyTableTypesArr={centNyTableTypesArr}
          partySize={selectedQuantity}
        />
      </>
    );

    const renderPlatinumVenueShift = (slots = []) => (
      <PlatinumVenueShift
        Analytics={Analytics}
        activeNotifications={activeNotifications}
        clearFilterButton={clearFilterButton}
        context={CONTEXT.INLINE_BOOKING}
        featureRecaptcha={venue.featureRecaptcha}
        onReservationSelect={onReservationSelect}
        reservations={slots}
        serviceTypeId={shift.serviceTypeId}
        serviceTypeName={serviceTypeName}
        venueId={venue.id}
        venueCity={venue.location.city}
        venueName={venue.name}
        centurionNyValue={centurionNyValue}
        centNyTableTypesArr={centNyTableTypesArr}
        partySize={selectedQuantity}
        amexLogoUrl={platinumNight?.amex_logo_image_url}
        headline={platinumNight?.label}
        title={platinumNight?.header}
        desc={platinumNight?.subheader}
        viewAllLabel={platinumNight?.all_events_button}
        viewAllLink={buildViewAllLink(platinumNight?.all_events_landing_page)}
      />
    );

    const platinumSlots = shift?.available?.filter((slot) => slot.exclusiveId === PLATINUM_EXCLUSIVE_ID);
    const nonPlatinumSlots = shift?.available?.filter((slot) => slot.exclusiveId !== PLATINUM_EXCLUSIVE_ID);
    const allSlots = shift?.available || [];

    const hasNonPlatinumSlots = nonPlatinumSlots && nonPlatinumSlots.length > 0;

    if (platinumNight !== null && shift.serviceTypeKey === 'dinner') {
      return (
        <div
          className={cx('ShiftInventory__shift', { 'ShiftInventory__shift--last': index === reservations.length - 1 })}
          key={serviceTypeName}
        >
          {hasNonPlatinumSlots && renderReservationButtonList(nonPlatinumSlots)}
          {renderPlatinumVenueShift(platinumSlots)}
        </div>
      );
    }

    return (
      <div
        className={cx('ShiftInventory__shift', { 'ShiftInventory__shift--last': index === reservations.length - 1 })}
        key={serviceTypeName}
      >
        {renderReservationButtonList(allSlots)}
      </div>
    );
  });

  const doubleBookingErrorMessage = (
    <div
      className="ShiftInventory__availability-message"
      id="double-booking-error"
    >
      <Icon component={Warning} name="warning" aria-hidden="true" />
      <div>{doubleBookingError}</div>
    </div>
  );

  const showLargePartyMessage = !!largePartyMessage
    && (selectedQuantity > maxQuantity)
    && !isClosedOnSelectedDate;

  const openButUnavailableMessage = () => {
    if ((!venue?.isGdaOnly || (venue?.isGdaOnly && isGdaUser))
      && !nextAvailableDate && !eventsExistOnDay) {
      return (
        <div className="ShiftInventory__availability-message">
          <>
            <InfoIcon />
            {showLargePartyMessage
              ? <div id="open-but-unavailable-without-future-large-party">{largePartyMessage}</div>
              : (
                <TranslationProvider
                  translationKey="no_availability_for_party_message"
                  translationValues={{ partySize: `${selectedQuantity}` }}
                >
                  <div id="open-but-unavailable-without-future">
                    Sorry, we don&apos;t currently have any tables available for
                    {' '}
                    {selectedQuantity}
                    .
                  </div>
                </TranslationProvider>
              )}
          </>
        </div>
      );
    }

    return (
      <div className="ShiftInventory__availability-message">
        {/* <!-- There IS availability for a future date --> */}
        {!!nextAvailableDate && !eventsExistOnDay && (
        <>
          <InfoIcon />
          <div id="open-but-unavailable-with-future">
            <TranslationProvider
              translationKey="next_online_availability_message"
              translationValues={{
                selectedDateFormatted,
                selectedQuantity: `${selectedQuantity}`,
              }}
            >
              <span>
                At the moment, there&apos;s no online availability for
                {' '}
                {selectedDateFormatted}
                . The next availability for
                {' '}
                {selectedQuantity}
                {' '}
                is&nbsp;
              </span>
            </TranslationProvider>
            <Link
              onClick={/* istanbul ignore next */ () => onDateSelect(nextAvailableDate)}
              data-test-id={`${testId}-next-available-table`}
            >
              {nextAvailableDateFormatted}
            </Link>
            .
          </div>
        </>
        )}
      </div>
    );
  };

  const openAndAvailableButNoSlotsMessage = (
    <div className="ShiftInventory__availability-message">
      <InfoIcon />
      <div id="open-and-available-but-no-slots">
        <TranslationProvider
          translationKey="other_times_availability_message"
          translationValues={{
            selectedDateFormatted,
            selectedQuantity: `${selectedQuantity}`,
            selectedTimeFormatted,
          }}
        >
          <span>
            At the moment, there&apos;s no online availability
            {' '}
            for
            {' '}
            {selectedDateFormatted}
            {' '}
            at
            {' '}
            {selectedTimeFormatted}
            , but there are&nbsp;
          </span>
        </TranslationProvider>
        <Link
          onClick={clearTimeFilter}
          data-test-id={`${testId}-other-times`}
        >
          <TranslationProvider translationKey="other_times">
            <span>other times</span>
          </TranslationProvider>
        </Link>
        .
      </div>
    </div>
  );

  const closedMessage = (
    // < !--Scenario when venue IS closed -->
    <div className="ShiftInventory__availability-message" id="venue-closed">
      <InfoIcon />
      <TranslationProvider translationKey="cant_accomodate_message">
        <div>Unfortunately, we can&apos;t accommodate that reservation. Please select another party size or date.</div>
      </TranslationProvider>
    </div>
  );

  if (loadingReservations) {
    return <Loader containerHeight="100px" />;
  }

  if (hasReservationSlots && selectedQuantity > maxQuantity) {
    if (largePartyMessage) {
      return (
        <>
          <div className="ShiftInventory__availability-message" id="shift-large-party">{largePartyMessage}</div>
          <hr />
        </>
      );
    }

    return (
      <>
        <TranslationProvider
          translationKey="no_availability_for_party_message"
          translationValues={{ partySize: `${selectedQuantity}` }}
        >
          <div className="ShiftInventory__availability-message" id="shift-large-party-unavailable">
            Sorry, we don&apos;t currently have any tables available for
            {' '}
            {selectedQuantity}
            .
          </div>
        </TranslationProvider>
        <hr />
      </>
    );
  }

  const hasTimeFilter = selectedTimeFormatted && selectedTimeFormatted !== ALL_DAY;
  const openButUnavailable = !hasReservationSlots && !hasTimeFilter && !eventsExistOnDay;
  const openAndAvailableButNoSlots = isInventoryAvailableForDate && !hasReservationSlots && hasTimeFilter;
  const shouldRenderNull = (venue?.isGda && venue?.isGdaOnly && !isGdaUser) || !reservations || reservations?.length === 0;

  return (
    <>
      {tableIsUnavailable && tableIsUnavailableMessage}
      {notifyData.shouldShowRecommendations && (
        <NotifyUnavailableModal
          Analytics={Analytics}
          notifyDate={notifyData.date}
          notifyPartySize={selectedQuantity}
          notifyStartTime={notifyData.time}
          notifyEndTime={notifyData.endTime}
          notifyTableType={notifyData.tableType}
          notifyVenueName={venue.name}
          venueGroupIds={venue.venueGroup?.venues}
          venues={recommendedVenues}
          isGdaUser={isGdaUser}
        />
      )}
      {notifyData.shouldShowTimeslotNotFound && <TableNotAvailable Analytics={Analytics} isGdaUser={isGdaUser} />}
      {doubleBookingError && doubleBookingErrorMessage}
      {openButUnavailable && openButUnavailableMessage()}
      {openAndAvailableButNoSlots && openAndAvailableButNoSlotsMessage}
      {isVenueClosed && closedMessage}
      {shouldRenderNull ? null : mappedReservations()}
    </>
  );
};

ShiftInventory.propTypes = {
  activeNotifications: PropTypes.shape({
    [PropTypes.number]: PropTypes.shape({
      start: PropTypes.string,
      end: PropTypes.string,
    }),
  }),
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }),
  clearTimeFilter: PropTypes.func.isRequired,
  doubleBookingError: PropTypes.string,
  eventsExistOnDay: PropTypes.bool,
  filterQuery: PropTypes.shape({
    day: PropTypes.string,
    party_size: PropTypes.number,
    time_filter: PropTypes.string,
  }).isRequired,
  isClosedOnSelectedDate: PropTypes.bool.isRequired,
  isGdaUser: PropTypes.bool,
  isInventoryAvailableForDate: PropTypes.bool.isRequired,
  isVenueClosed: PropTypes.bool.isRequired,
  largePartyMessage: PropTypes.string,
  loadingReservations: PropTypes.bool,
  maxQuantity: PropTypes.number,
  nextAvailableDate: PropTypes.oneOfType([PropTypes.oneOf([null]), PropTypes.string]),
  nextAvailableDateFormatted: PropTypes.string,
  notifyData: PropTypes.shape({
    shouldShowRecommendations: PropTypes.bool,
    shouldShowTableConfigIdNotFound: PropTypes.bool,
    shouldShowTimeslotNotFound: PropTypes.bool,
    tableType: PropTypes.string,
    date: PropTypes.oneOfType([PropTypes.oneOf([null]), momentPropTypes.momentObj]),
    time: PropTypes.oneOfType([PropTypes.oneOf([null]), momentPropTypes.momentObj]),
    endTime: PropTypes.oneOfType([PropTypes.oneOf([null]), momentPropTypes.momentObj]),
  }),
  onDateSelect: PropTypes.func.isRequired,
  onReservationSelect: PropTypes.func.isRequired,
  platinumNight: PropTypes.shape({
    amex_logo_image_url: PropTypes.string,
    label: PropTypes.string,
    header: PropTypes.string,
    subheader: PropTypes.string,
    all_events_button: PropTypes.string,
    all_events_landing_page: PropTypes.string,
  }),
  recommendedVenues: PropTypes.arrayOf(PropTypes.shape({})),
  reservations: ReservationShape.isRequired,
  selectedDateFormatted: momentPropTypes.momentObj.isRequired,
  selectedTimeFormatted: PropTypes.string,
  selectedQuantity: PropTypes.number,
  tableIsUnavailable: PropTypes.bool,
  venue: VenueShape.isRequired,
  shopSmallValue: PropTypes.string,
  centurionNyValue: PropTypes.bool,
  centNyServiceTypes: PropTypes.string,
  centNyTableTypes: PropTypes.string,
};

ShiftInventory.defaultProps = {
  activeNotifications: {},
  Analytics: undefined,
  doubleBookingError: undefined,
  eventsExistOnDay: false,
  isGdaUser: false,
  largePartyMessage: undefined,
  loadingReservations: false,
  maxQuantity: 25,
  nextAvailableDate: null,
  nextAvailableDateFormatted: null,
  notifyData: {
    shouldShowRecommendations: false,
    shouldShowTableConfigIdNotFound: false,
    shouldShowTimeslotNotFound: false,
    date: null,
    tableType: undefined,
    time: null,
  },
  platinumNight: null,
  recommendedVenues: [],
  selectedTimeFormatted: '',
  selectedQuantity: 2,
  tableIsUnavailable: false,
  shopSmallValue: '',
  centurionNyValue: false,
  centNyServiceTypes: '',
  centNyTableTypes: '',
};

ShiftInventory.displayName = 'ShiftInventory';
export default ShiftInventory;
